package com.flowyride.feature.settings.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigationevent.NavigationEventInfo
import androidx.navigationevent.compose.NavigationBackHandler
import androidx.navigationevent.compose.rememberNavigationEventState
import com.flowyride.core.designsystem.component.SavedPlaceBadge
import com.flowyride.core.domain.place.SavedPlace
import com.flowyride.feature.settings.SettingsAction
import com.flowyride.feature.settings.SettingsUiState
import com.flowyride.feature.settings.SettingsViewModel
import com.flowyride.feature.settings.resources.Res
import com.flowyride.feature.settings.resources.back
import com.flowyride.feature.settings.resources.places_empty
import com.flowyride.feature.settings.resources.places_hint
import com.flowyride.feature.settings.resources.retry
import com.flowyride.feature.settings.resources.section_account
import com.flowyride.feature.settings.resources.section_places
import com.flowyride.feature.settings.resources.settings_title
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

/**
 * Full-screen settings, over the planner (kept composed underneath).
 *
 * @param accountSection the account's rows (who is signed in, password, admin, sign out): owned by the account feature.
 */
@Composable
fun SettingsRoute(
    onBack: () -> Unit,
    accountSection: @Composable () -> Unit,
    viewModel: SettingsViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    // Android back button / gesture: back to the planner rather than out of the app.
    NavigationBackHandler(rememberNavigationEventState(NavigationEventInfo.None), onBackCompleted = onBack)
    SettingsScreen(state, viewModel::onAction, onBack, accountSection)
}

/** One section per subject: new settings get a section of their own below the existing ones. */
@Composable
fun SettingsScreen(
    state: SettingsUiState,
    onAction: (SettingsAction) -> Unit,
    onBack: () -> Unit,
    accountSection: @Composable () -> Unit,
) {
    Surface(Modifier.fillMaxSize()) {
        Column(Modifier.fillMaxSize().safeDrawingPadding()) {
            Row(Modifier.fillMaxWidth().padding(horizontal = 4.dp), verticalAlignment = Alignment.CenterVertically) {
                TextButton(onClick = onBack) { Text(stringResource(Res.string.back)) }
                Text(stringResource(Res.string.settings_title), style = MaterialTheme.typography.titleLarge)
            }
            if (state.loadingPlaces) LinearProgressIndicator(Modifier.fillMaxWidth()) else HorizontalDivider()
            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.TopCenter) {
                LazyColumn(
                    Modifier.widthIn(max = 640.dp).fillMaxWidth(),
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    section(Res.string.section_account) { accountSection() }
                    placesSection(state, onAction)
                }
            }
        }
    }

    state.editing?.let { place ->
        SavedPlaceEditDialog(
            place = place,
            saving = state.saving,
            error = state.error,
            onSave = { onAction(SettingsAction.Save(place.id, it)) },
            onDelete = { onAction(SettingsAction.Delete(place.id)) },
            onDismiss = { onAction(SettingsAction.CancelEdit) },
        )
    }
}

private fun LazyListScope.section(title: org.jetbrains.compose.resources.StringResource, content: @Composable () -> Unit) {
    item(key = title.key) {
        Column(Modifier.padding(top = 8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text(stringResource(title), style = MaterialTheme.typography.titleMedium, color = MaterialTheme.colorScheme.primary)
            content()
        }
    }
}

private fun LazyListScope.placesSection(state: SettingsUiState, onAction: (SettingsAction) -> Unit) {
    section(Res.string.section_places) {
        val hint = if (state.places.isEmpty()) Res.string.places_empty else Res.string.places_hint
        if (!state.loadingPlaces) {
            Text(stringResource(hint), style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
        // The dialog shows its own errors.
        state.error?.takeIf { state.editing == null }?.let { error ->
            Text(stringResource(error), color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodyMedium)
            OutlinedButton(onClick = { onAction(SettingsAction.Refresh) }) { Text(stringResource(Res.string.retry)) }
        }
    }
    items(state.places, key = { "place-${it.id}" }) { place ->
        PlaceRow(place, onClick = { onAction(SettingsAction.Edit(place)) })
    }
}

@Composable
private fun PlaceRow(place: SavedPlace, onClick: () -> Unit) {
    Row(
        Modifier.fillMaxWidth().clickable(onClick = onClick).padding(vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(16.dp),
    ) {
        Box(Modifier.widthIn(min = 36.dp), contentAlignment = Alignment.Center) {
            SavedPlaceBadge(place.emoji, place.color, size = if (place.emoji == null) 20.dp else 36.dp)
        }
        Column(Modifier.weight(1f)) {
            Text(place.name, style = MaterialTheme.typography.bodyLarge, maxLines = 1, overflow = TextOverflow.Ellipsis)
            place.detail?.let {
                Text(it, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant, maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
        }
    }
}
